import { memo, useCallback, useMemo, useEffect } from 'react';
import { cn, TabControl } from '@divlab/divanui';

import * as ModalWindows from '@Stores/ModalWindows';
import Button from '@UI/Button';
import ReviewGallery from '@Components/Review/elems/ReviewGallery';
import useTranslation from '@Queries/useTranslation';
import useMedias from '@Hooks/useMedias';
import styles from './ReviewsSection.module.css';

import type { ReviewData, ReviewPhotoData } from '@Types/Review';
import type { FC, HTMLAttributes, MouseEvent } from 'react';

export type SelectedReviewsData = 'all' | 'withPhoto';

interface IndexesData {
  reviewIndex?: number;
  photoIndex?: number;
}

export interface ReviewsSectionProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  reviews?: ReviewData[];
  onAddReview?: () => void;
  onChangeSelectedReviews?: (e, kind: SelectedReviewsData) => void;
}

const ReviewsSection: FC<ReviewsSectionProps> = (props) => {
  const { className, reviews = [], onAddReview, onChangeSelectedReviews, ...restProps } = props;
  const { isDesktopM, isDesktop, isMobileM, isMobile } = useMedias();
  const { t } = useTranslation();

  const countPhotoInGallery = useMemo(() => {
    if (isMobile) return 4;
    if (isMobileM) return 5;
    if (isDesktop) return 7;

    return 8;
  }, [isDesktop, isMobileM, isMobile]);

  const countReviews = useMemo(() => {
    const siteReviews = reviews.filter((review) => review.source === 'site');
    const reviewWithPhoto = siteReviews.filter((item) => item.photos?.length > 0);

    return { all: siteReviews.length, withPhoto: reviewWithPhoto.length };
  }, [reviews]);

  const tabs = [
    {
      key: 'all',
      title: (
        <div className={styles.control}>
          {t('reviews.all')}
          <span className={styles.controlCount}>{countReviews.all}</span>
        </div>
      ),
      onClick: (e: MouseEvent) => {
        onChangeSelectedReviews(e, 'all');
      },
    },
    {
      key: 'withPhoto',
      title: (
        <div className={styles.control}>
          {t('reviews.only-with-photo')}
          <span className={styles.controlCount}>{countReviews.withPhoto}</span>
        </div>
      ),
      onClick: (e: MouseEvent) => {
        onChangeSelectedReviews(e, 'withPhoto');
      },
    },
  ];

  const photos = useMemo((): ReviewPhotoData[] => {
    return reviews.reduce((prevReviews, review) => [...prevReviews, ...review.photos], []);
  }, [reviews]);

  const hasPhotos = photos.length > 0;

  const getReviewAndPhotoIndexes = useCallback(
    (photoId: number) => {
      const result: IndexesData = {};

      reviews.some((item: ReviewData, itemIndex) => {
        const photoItemIndex = item.photos.findIndex((photo) => photo.id === photoId);
        if (photoItemIndex !== -1) {
          result.reviewIndex = itemIndex;
          result.photoIndex = photoItemIndex;
          return true;
        }

        return false;
      });

      return result;
    },
    [reviews],
  );

  const handleClickReviewImage = useCallback(
    (e: MouseEvent, photoId: number) => {
      const indexes = getReviewAndPhotoIndexes(photoId);

      if (!indexes.reviewIndex && indexes.reviewIndex !== 0) return;

      ModalWindows.open('Review', { indexes, reviews });
    },
    [getReviewAndPhotoIndexes, reviews],
  );

  useEffect(() => {
    if (!window.location.hash.match(/^#review-\d+$/)) return;

    const splitedHash = window.location.hash.split('-');
    const photoId = splitedHash[1];
    const indexes = getReviewAndPhotoIndexes(Number(photoId));

    if (!indexes.reviewIndex && indexes.reviewIndex !== 0) return;

    ModalWindows.open('Review', { indexes, reviews });
  }, [reviews, getReviewAndPhotoIndexes]);

  const addReviewButton = (
    <Button className={styles.button} onClick={onAddReview} data-testid='button-leave-feedback'>
      {t('reviews.add-review')}
    </Button>
  );

  return (
    <div {...restProps} className={cn(styles.section, className)}>
      {countReviews.all > 0 && (
        <>
          <div className={styles.head}>
            <div
              className={styles.headMain}
              itemProp='aggregateRating'
              itemScope
              itemType='http://schema.org/AggregateRating'
            >
              <div className={styles.title}>
                {t('reviews.title')}
                <span className={styles.count}>{countReviews.all}</span>
              </div>
              <meta itemProp='reviewCount' content={countReviews.all.toString()} />
            </div>
            {(!hasPhotos || isMobile) && addReviewButton}
          </div>

          {hasPhotos && (
            <>
              <ReviewGallery
                photos={photos}
                count={countPhotoInGallery}
                className={styles.reviewGallery}
                cnCount={styles.cnCount}
                onClickImage={handleClickReviewImage}
              />

              <div className={styles.wrapper}>
                {countReviews.withPhoto && (
                  <TabControl
                    className={styles.toggle}
                    itemList={tabs}
                    defaultSelected='all'
                    size={isDesktopM ? 'm' : 'l'}
                  />
                )}
                {!isMobile && addReviewButton}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default memo(ReviewsSection);
